import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  Users,
  Loader2,
  Lock,
  Eye,
  FileEdit,
  UserPlus,
  X,
  Check,
  Settings,
  Calendar,
  CreditCard,
  Mail,
  BarChart3,
  LayoutDashboard,
  Heart,
  RotateCcw,
  Info
} from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission module icons
const moduleIcons = {
  dashboard: LayoutDashboard,
  patients: Users,
  appointments: Calendar,
  billing: CreditCard,
  team: Users,
  settings: Settings,
  communications: Mail,
  reports: BarChart3,
  memberships: Heart
};

// Permission action labels
const actionLabels = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  cancel: 'Cancel',
  process: 'Process',
  refund: 'Refund',
  invite: 'Invite',
  remove: 'Remove',
  send: 'Send',
  export: 'Export',
  schedule: 'Schedule'
};

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [impersonationSettings, setImpersonationSettings] = useState({ allowed_roles: ['admin'] });
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {},
    can_impersonate_patients: false
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchImpersonationSettings();
  }, []);
  
  const fetchImpersonationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clinic/impersonation-privileges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImpersonationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch impersonation settings:', error);
    }
  };
  
  const updateImpersonationPrivilege = async (roleName, canImpersonate) => {
    try {
      const token = localStorage.getItem('token');
      const currentRoles = impersonationSettings.allowed_roles || ['admin'];
      let newRoles;
      
      if (canImpersonate) {
        newRoles = [...new Set([...currentRoles, roleName])];
      } else {
        newRoles = currentRoles.filter(r => r !== roleName);
      }
      
      // Ensure admin is always included
      if (!newRoles.includes('admin')) {
        newRoles.push('admin');
      }
      
      await axios.post(
        `${API_URL}/api/clinic/impersonation-privileges`,
        { allowed_roles: newRoles, enabled: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImpersonationSettings(prev => ({ ...prev, allowed_roles: newRoles }));
      toast.success(`Patient View privilege ${canImpersonate ? 'granted' : 'revoked'} for ${roleName}`);
    } catch (error) {
      console.error('Failed to update impersonation privilege:', error);
      toast.error('Failed to update privilege');
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/roles/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const initializeFormPermissions = () => {
    const perms = {};
    Object.keys(permissions).forEach(module => {
      perms[module] = [];
    });
    return perms;
  };

  const handleCreateRole = () => {
    setFormData({
      name: '',
      description: '',
      permissions: initializeFormPermissions()
    });
    setShowCreateDialog(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: { ...role.permissions }
    });
    setShowEditDialog(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };

  const togglePermission = (module, action) => {
    setFormData(prev => {
      const modulePerms = prev.permissions[module] || [];
      const newPerms = modulePerms.includes(action)
        ? modulePerms.filter(a => a !== action)
        : [...modulePerms, action];
      
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: newPerms
        }
      };
    });
  };

  const toggleAllModulePermissions = (module) => {
    const moduleData = permissions[module];
    if (!moduleData) return;
    
    const currentPerms = formData.permissions[module] || [];
    const allActions = moduleData.actions;
    const hasAll = allActions.every(a => currentPerms.includes(a));
    
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: hasAll ? [] : [...allActions]
      }
    }));
  };

  const resetToDefault = async () => {
    if (!selectedRole?.is_system) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/roles/${selectedRole.id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Permissions reset to default');
      setShowEditDialog(false);
      fetchRoles();
    } catch (error) {
      console.error('Failed to reset role:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const saveRole = async (isEdit = false) => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isEdit) {
        await axios.put(
          `${API_URL}/api/roles/${selectedRole.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role updated successfully');
        // Show info toast about users needing to re-login
        toast.info('Users with this role need to log out and log in again to see the new permissions', {
          duration: 6000,
        });
      } else {
        await axios.post(
          `${API_URL}/api/roles`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role created successfully');
      }
      
      setShowCreateDialog(false);
      setShowEditDialog(false);
      fetchRoles();
    } catch (error) {
      console.error('Failed to save role:', error);
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/roles/${selectedRole.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted successfully');
      setShowDeleteDialog(false);
      fetchRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const countPermissions = (role) => {
    let count = 0;
    Object.values(role.permissions || {}).forEach(actions => {
      count += actions.length;
    });
    return count;
  };

  const PermissionEditor = () => (
    <div className="space-y-4 max-h-[400px] overflow-y-auto">
      {Object.entries(permissions).map(([module, moduleData]) => {
        const Icon = moduleIcons[module] || Shield;
        const modulePerms = formData.permissions[module] || [];
        const allSelected = moduleData.actions.every(a => modulePerms.includes(a));
        const someSelected = moduleData.actions.some(a => modulePerms.includes(a));
        
        return (
          <div key={module} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-teal-600" />
                <span className="font-medium">{moduleData.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleAllModulePermissions(module)}
                className="text-xs"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <p className="text-sm text-gray-500 mb-3">{moduleData.description}</p>
            <div className="flex flex-wrap gap-2">
              {moduleData.actions.map(action => (
                <label
                  key={action}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                    modulePerms.includes(action)
                      ? 'bg-teal-50 border-teal-300 text-teal-700'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Checkbox
                    checked={modulePerms.includes(action)}
                    onCheckedChange={() => togglePermission(module, action)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{actionLabels[action] || action}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Role Management</h2>
          <p className="text-gray-600">Manage team roles and permissions</p>
        </div>
        <Button onClick={handleCreateRole} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Custom Role
        </Button>
      </div>

      {/* Info Alert about permission changes */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Note:</strong> When you modify role permissions, users with that role need to <strong>log out and log in again</strong> to see the updated permissions in their portal. Changes are saved immediately but user sessions cache their permissions.
        </AlertDescription>
      </Alert>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Roles
          </CardTitle>
          <CardDescription>
            Predefined and custom roles for your clinic
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Patient View</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => {
                const roleName = role.name.toLowerCase();
                // Check if role can impersonate - handle both 'admin' and 'administrator'
                const isAdmin = roleName === 'admin' || roleName === 'administrator';
                const canImpersonate = impersonationSettings.allowed_roles?.includes(roleName) || 
                                       impersonationSettings.allowed_roles?.includes('admin') && isAdmin ||
                                       isAdmin;
                return (
                <TableRow key={role.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-teal-600" />
                      <span className="font-medium">{role.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600 max-w-xs truncate">
                    {role.description || '-'}
                  </TableCell>
                  <TableCell>
                    {role.is_system ? (
                      <Badge variant="secondary">
                        <Lock className="h-3 w-3 mr-1" />
                        System
                      </Badge>
                    ) : (
                      <Badge className="bg-teal-100 text-teal-700">
                        Custom
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {countPermissions(role)} permissions
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <Badge className="bg-teal-100 text-teal-700">
                        <Eye className="h-3 w-3 mr-1" />
                        Always On
                      </Badge>
                    ) : (
                      <Button
                        variant={canImpersonate ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateImpersonationPrivilege(role.name.toLowerCase(), !canImpersonate)}
                        className={canImpersonate ? "bg-teal-600 hover:bg-teal-700" : ""}
                        title={canImpersonate ? "Click to revoke Patient View privilege" : "Click to grant Patient View privilege"}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {canImpersonate ? 'Enabled' : 'Disabled'}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRole(role)}
                        title="Edit role permissions"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRole(role)}
                        disabled={role.is_system}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={role.is_system ? "Cannot delete system roles" : "Delete role"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Permission Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Permission Modules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(permissions).map(([module, moduleData]) => {
              const Icon = moduleIcons[module] || Shield;
              return (
                <div key={module} className="flex items-center gap-2 text-sm">
                  <Icon className="h-4 w-4 text-gray-500" />
                  <span>{moduleData.name}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Patient View Privilege Info */}
      <Card className="border-teal-200 bg-teal-50/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5 text-teal-600" />
            Patient View Privilege
          </CardTitle>
          <CardDescription>
            Control which roles can view the patient portal as patients
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="bg-white border-teal-200">
            <Info className="h-4 w-4 text-teal-600" />
            <AlertDescription className="text-gray-700">
              <strong>What is Patient View?</strong> This privilege allows staff members to view the patient portal 
              exactly as a specific patient sees it. It's useful for helping tech-illiterate patients navigate their portal, 
              troubleshooting issues, or filling out forms on their behalf. All Patient View sessions are logged for security and audit purposes.
              <br /><br />
              <strong>Note:</strong> Admin role always has this privilege and cannot be disabled.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Custom Role</DialogTitle>
            <DialogDescription>
              Define a new role with specific permissions for your team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Role Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Front Desk"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this role"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Permissions</Label>
              <PermissionEditor />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => saveRole(false)} 
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Role: {selectedRole?.name}</DialogTitle>
            <DialogDescription>
              {selectedRole?.is_system 
                ? "Customize permissions for this system role (name cannot be changed)"
                : "Modify role name and permissions"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Role Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={selectedRole?.is_system}
                  className={selectedRole?.is_system ? "bg-gray-100" : ""}
                />
                {selectedRole?.is_system && (
                  <p className="text-xs text-gray-500">System role names cannot be changed</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={selectedRole?.is_system}
                  className={selectedRole?.is_system ? "bg-gray-100" : ""}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Permissions</Label>
              <PermissionEditor />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            {selectedRole?.is_system && selectedRole?.has_custom_permissions && (
              <Button 
                variant="outline"
                onClick={resetToDefault}
                disabled={saving}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Default
              </Button>
            )}
            <Button 
              onClick={() => saveRole(true)} 
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role "{selectedRole?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteRole}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
